/**
 * GroupedTile — renders any existing MAIN-xx grouped series inside the uniform
 * DashTile frame (quick-261104-ia3). Used by the Marketing and Finance tabs so
 * the legacy series keep their data but get the new look and plain-English
 * hovers. Shapes handled: {label, count|value} categories, `stages` maps
 * (stacked), quarter lines (value 0..1 or days), and MAIN-44 quota bars.
 */
import ReactECharts from 'echarts-for-react';
import { useMemo } from 'react';

import { BAR_RADIUS, INK, SERIES, baseOption, stageColor, tipHead, tipNote, tipRow } from '../../lib/exec/chart.js';
import { money, num, pct } from '../../lib/exec/fmt.js';

import { BloodDrive } from './controls.js';
import { DashTile, Legend, TileEmpty, type TileSize } from './DashTile.js';

import type { MainGroupedResponse } from '@saledash/shared';
import type { EChartsOption } from 'echarts';

type Unit = 'count' | 'money' | 'percent' | 'days';
type Kind = 'hbars' | 'bars' | 'line' | 'stack' | 'quota';

export interface GroupedTileConfig {
  title: string;
  info: string;
  unit: Unit;
  kind: Kind;
  sub: string;
  footer: string;
  /** Headline: 'total' (sum / total_value) or 'none'. */
  headline?: 'total' | 'pooled' | 'none';
}

export const GROUPED_TILE_CONFIG: Record<string, GroupedTileConfig> = {
  'MAIN-27': { title: 'Inbound leads by status', info: 'New inbound leads from the last 30 days, grouped by where they are now: new, working, qualified, disqualified and so on.', unit: 'count', kind: 'hbars', sub: 'inbound leads, last 30 days', footer: 'Salesforce leads, inbound sources only' },
  'MAIN-28': { title: 'Inbound leads by source', info: 'Where the last 30 days of inbound leads came from: website, events, partners, paid campaigns and so on.', unit: 'count', kind: 'hbars', sub: 'inbound leads, last 30 days', footer: 'Salesforce Lead Source' },
  'MAIN-29': { title: 'Inbound leads by industry', info: 'The industries of the last 30 days of inbound leads. Useful to see whether marketing is attracting our best-fit markets.', unit: 'count', kind: 'hbars', sub: 'inbound leads, last 30 days', footer: 'Salesforce lead industry' },
  'MAIN-30': { title: 'Working leads by owner', info: 'Inbound leads from the last 30 days that someone is actively working, by the person who owns them.', unit: 'count', kind: 'hbars', sub: 'leads being worked', footer: 'Leads created in the last 30 days' },
  'MAIN-31': { title: 'Disqualified leads by reason', info: 'Why leads were ruled out. A big "not a fit" bar means marketing is reaching the wrong audience; "no response" means follow-up is missing.', unit: 'count', kind: 'hbars', sub: 'disqualified leads', footer: 'Salesforce disqualification reason' },
  'MAIN-32': { title: 'Marketing pipeline by channel', info: 'Dollars of open pipeline that marketing sourced, by the channel that brought the lead in.', unit: 'money', kind: 'hbars', sub: 'marketing-sourced pipeline', footer: 'Opportunities from marketing-sourced leads' },
  'MAIN-33': { title: 'Marketing pipeline by month', info: 'Marketing-sourced pipeline by the month it was created, stacked by the stage each deal sits in today.', unit: 'money', kind: 'stack', sub: 'by month created', footer: 'Stacked by current stage' },
  'MAIN-49': { title: 'Marketing win rate', info: 'Of marketing-sourced deals that finished each quarter, the share we won.', unit: 'percent', kind: 'line', sub: 'pooled, last 5 quarters', footer: 'Won ÷ (won + lost), marketing-sourced', headline: 'pooled' },
  'MAIN-50': { title: 'Time in stage: marketing vs other', info: 'For deals we won, the average days spent in each stage, comparing marketing-sourced deals with everything else.', unit: 'days', kind: 'stack', sub: 'avg days per stage, won deals', footer: 'Stacked by stage', headline: 'none' },
  'MAIN-51': { title: 'Time in stage: top 3 sources', info: 'For deals we won, the average days in each stage for our three biggest lead sources.', unit: 'days', kind: 'stack', sub: 'avg days per stage, won deals', footer: 'Stacked by stage', headline: 'none' },
  'MAIN-34': { title: 'Lead to opportunity by channel', info: 'The share of leads from each marketing channel that turned into an opportunity. Higher means the channel brings better leads.', unit: 'percent', kind: 'hbars', sub: 'leads that became opps', footer: 'By lead channel', headline: 'none' },
  'MAIN-35': { title: 'Win rate by channel', info: 'Of marketing deals that finished, the share we won, by channel.', unit: 'percent', kind: 'hbars', sub: 'won ÷ finished, by channel', footer: 'By lead channel', headline: 'none' },
  'MAIN-36': { title: 'Sales cycle by channel', info: 'Average days from opportunity to win for marketing-sourced deals, by channel. Shorter is better.', unit: 'days', kind: 'hbars', sub: 'avg days to win', footer: 'By lead channel', headline: 'none' },
  'MAIN-42': { title: 'Cost per lead by channel', info: 'Marketing spend divided by the leads each channel produced. Lower means cheaper leads.', unit: 'money', kind: 'hbars', sub: 'spend ÷ leads', footer: 'Finance upload · spend by channel', headline: 'none' },
  'MAIN-43': { title: 'Cost per opportunity by channel', info: 'Marketing spend divided by the opportunities each channel produced. Lower means pipeline is cheaper to create.', unit: 'money', kind: 'hbars', sub: 'spend ÷ opportunities', footer: 'Finance upload · spend by channel', headline: 'none' },
  'MAIN-44': { title: 'AE bookings vs quota', info: 'Each account executive’s closed-won bookings this quarter, filling toward their quota. The line is the quota; a ✓ means they hit it.', unit: 'money', kind: 'quota', sub: 'team, this quarter', footer: 'Quota from Salesforce forecasting' },
};

function fmt(unit: Unit, v: number | null | undefined): string {
  if (v == null) return '—';
  if (unit === 'money') return money(v);
  if (unit === 'percent') return pct(v);
  if (unit === 'days') return `${String(Math.round(v))} days`;
  return num(v);
}

function valueOf(b: Record<string, unknown>, unit: Unit): number {
  if (b.stages && typeof b.stages === 'object') {
    return Object.values(b.stages as Record<string, number>).reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0);
  }
  if (unit === 'count' && typeof b.count === 'number') return b.count;
  if (typeof b.value === 'number') return b.value;
  if (typeof b.count === 'number') return b.count;
  return 0;
}

export function GroupedTile({
  metricId,
  data,
  size = 'lg',
}: {
  metricId: string;
  data: MainGroupedResponse | undefined;
  size?: TileSize;
}): React.JSX.Element {
  const cfg = GROUPED_TILE_CONFIG[metricId];
  const buckets = (data?.buckets ?? []) as Record<string, unknown>[];

  const view = useMemo((): { option: EChartsOption | null; legend: { label: string; color: string }[] } => {
    if (!cfg || buckets.length === 0 || cfg.kind === 'quota') return { option: null, legend: [] };
    const base = baseOption({ yMoney: cfg.unit === 'money', yPercent: cfg.unit === 'percent', yDays: cfg.unit === 'days' });
    const labels = buckets.map((b) => String(b.label));

    if (cfg.kind === 'stack') {
      const stageNames: string[] = [];
      for (const b of buckets) for (const s of Object.keys((b.stages ?? {}))) if (!stageNames.includes(s)) stageNames.push(s);
      return {
        legend: stageNames.map((s) => ({ label: s, color: stageColor(s) })),
        option: {
          ...base,
          xAxis: { ...(base.xAxis as object), data: labels },
          tooltip: {
            ...(base.tooltip as object),
            formatter: (params: unknown) => {
              const i = (params as { dataIndex: number }[])[0]?.dataIndex ?? 0;
              const st = (buckets[i]?.stages ?? {}) as Record<string, number>;
              return tipHead(labels[i] ?? '') + stageNames.filter((s) => (st[s] ?? 0) > 0).map((s) => tipRow(stageColor(s), s, fmt(cfg.unit, st[s]))).join('') + tipNote(cfg.info);
            },
          },
          series: stageNames.map((s, si) => ({
            name: s,
            type: 'bar' as const,
            stack: 't',
            barMaxWidth: 26,
            data: buckets.map((b) => ((b.stages ?? {}) as Record<string, number>)[s] ?? 0),
            itemStyle: { color: stageColor(s), borderColor: '#fff', borderWidth: 1, borderRadius: si === stageNames.length - 1 ? BAR_RADIUS : 0 },
          })),
        },
      };
    }

    if (cfg.kind === 'line') {
      return {
        legend: [],
        option: {
          ...base,
          xAxis: { ...(base.xAxis as object), data: labels },
          tooltip: {
            ...(base.tooltip as object),
            axisPointer: { type: 'line', lineStyle: { color: INK.axis } },
            formatter: (params: unknown) => {
              const i = (params as { dataIndex: number }[])[0]?.dataIndex ?? 0;
              const b = buckets[i];
              return b ? tipHead(`${labels[i] ?? ''} · ${fmt(cfg.unit, valueOf(b, cfg.unit))}`) + tipRow(null, 'Deals behind it', num(typeof b.count === 'number' ? b.count : null)) + tipNote(cfg.info) : '';
            },
          },
          series: [{ type: 'line', data: buckets.map((b) => valueOf(b, cfg.unit)), symbolSize: 8, lineStyle: { width: 2, color: SERIES.blue }, itemStyle: { color: SERIES.blue, borderColor: '#fff', borderWidth: 2 } }],
        },
      };
    }

    // bars / hbars — sorted biggest first for hbars, top 10.
    const rows = buckets
      .map((b) => ({ label: String(b.label), v: valueOf(b, cfg.unit) }))
      .filter((r) => r.v > 0);
    const sorted = cfg.kind === 'hbars' ? rows.sort((a, b) => b.v - a.v).slice(0, 10) : rows;
    const tip = (params: unknown): string => {
      const r = sorted[(params as { dataIndex: number }[])[0]?.dataIndex ?? 0];
      return r ? tipHead(`${r.label} · ${fmt(cfg.unit, r.v)}`) + tipNote(cfg.info) : '';
    };
    if (cfg.kind === 'hbars') {
      return {
        legend: [],
        option: {
          ...base,
          grid: { left: 130, right: 52, top: 4, bottom: 4 },
          xAxis: { type: 'value', show: false },
          yAxis: { type: 'category', inverse: true, data: sorted.map((r) => r.label), axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: INK.secondary, fontSize: 11, width: 120, overflow: 'truncate' } },
          tooltip: { ...(base.tooltip as object), formatter: tip },
          series: [{ type: 'bar', barWidth: '62%', barMaxWidth: 22, data: sorted.map((r) => r.v), itemStyle: { color: SERIES.blue, borderRadius: [0, 4, 4, 0] }, label: { show: true, position: 'right', color: INK.secondary, fontSize: 10, formatter: (p: unknown) => fmt(cfg.unit, (p as { value: number }).value) } }],
        },
      };
    }
    return {
      legend: [],
      option: {
        ...base,
        xAxis: { ...(base.xAxis as object), data: sorted.map((r) => r.label) },
        tooltip: { ...(base.tooltip as object), formatter: tip },
        series: [{ type: 'bar', barMaxWidth: 26, data: sorted.map((r) => r.v), itemStyle: { color: SERIES.blue, borderRadius: BAR_RADIUS } }],
      },
    };
  }, [cfg, buckets]);

  if (!cfg) return <DashTile title={metricId} info="" size={size} />;

  let headline: string | null = null;
  if (cfg.kind === 'quota') {
    const act = buckets.reduce((s, b) => s + (typeof b.actual === 'number' ? b.actual : 0), 0);
    const quota = buckets.reduce((s, b) => s + (typeof b.quota === 'number' ? b.quota : 0), 0);
    headline = quota > 0 ? `${money(act)} of ${money(quota)}` : money(act);
  } else if (cfg.headline !== 'none') {
    if (cfg.headline === 'pooled' || data?.total_value != null) headline = fmt(cfg.unit, data?.total_value ?? null);
    else headline = fmt(cfg.unit, buckets.reduce((s, b) => s + valueOf(b, cfg.unit), 0));
  }

  return (
    <DashTile title={cfg.title} info={cfg.info} headline={headline ?? undefined} sub={cfg.sub} size={size} footer={cfg.footer} testId={`grouped-${metricId}`}>
      {buckets.length === 0 ? (
        <TileEmpty text={data?.placeholder_text ?? (data ? 'No data in this window.' : 'Loading…')} />
      ) : cfg.kind === 'quota' ? (
        <div className="flex h-full items-end gap-2 overflow-x-auto pb-1">
          {buckets.map((b) => (
            <BloodDrive
              key={String(b.label)}
              label={String(b.label)}
              value={typeof b.actual === 'number' ? b.actual : 0}
              target={typeof b.quota === 'number' && b.quota > 0 ? b.quota : null}
              height={170}
            />
          ))}
        </div>
      ) : view.option ? (
        <div className="flex h-full flex-col">
          <div className="min-h-0 flex-1">
            <ReactECharts option={view.option} style={{ height: '100%', width: '100%' }} notMerge />
          </div>
          {view.legend.length > 1 ? <Legend items={view.legend} /> : null}
        </div>
      ) : (
        <TileEmpty text="No data in this window." />
      )}
    </DashTile>
  );
}
